import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
  Loader2,
} from "lucide-react"
import { useTheme } from "next-themes"
import { Toaster as Sonner, type ToasterProps } from "sonner"

import { ToastConfig } from "@/lib/toast/config"

/**
 * Toast Styles Configuration
 *
 * Uses shadcn/Tailwind color classes for consistent styling across light/dark modes.
 * Each toast type has its own color scheme based on semantic meaning.
 */
const toastStyles = {
  // Base toast styles - structural only, colors come from type-specific or CSS vars
  toast:
    "group toast group-[.toaster]:border-border group-[.toaster]:shadow-lg group-[.toaster]:rounded-lg",
  // Description text - semi-transparent for visual hierarchy on any background
  description: "!text-black/60 dark:!text-white/60",
  // Action button styling
  actionButton:
    "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground group-[.toast]:hover:bg-primary/90 group-[.toast]:font-medium group-[.toast]:text-sm group-[.toast]:px-3 group-[.toast]:py-1.5 group-[.toast]:rounded-md",
  // Cancel button styling
  cancelButton:
    "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground group-[.toast]:hover:bg-muted/80 group-[.toast]:font-medium group-[.toast]:text-sm group-[.toast]:px-3 group-[.toast]:py-1.5 group-[.toast]:rounded-md",
  // Close button styling
  closeButton:
    "group-[.toast]:bg-popover group-[.toast]:text-muted-foreground group-[.toast]:border-border group-[.toast]:hover:bg-muted group-[.toast]:hover:text-foreground",

  // Type-specific styles - Success (neutral background, green icon provides semantic meaning)
  success:
    "group toast group-[.toaster]:!bg-popover group-[.toaster]:!text-popover-foreground group-[.toaster]:!border-border",
  // Type-specific styles - Error (red)
  error:
    "group toast group-[.toaster]:!bg-red-50 group-[.toaster]:!text-red-900 group-[.toaster]:!border-red-200 dark:group-[.toaster]:!bg-red-950 dark:group-[.toaster]:!text-red-100 dark:group-[.toaster]:!border-red-800",
  // Type-specific styles - Warning (amber/orange)
  warning:
    "group toast group-[.toaster]:!bg-amber-50 group-[.toaster]:!text-amber-900 group-[.toaster]:!border-amber-200 dark:group-[.toaster]:!bg-amber-950 dark:group-[.toaster]:!text-amber-100 dark:group-[.toaster]:!border-amber-800",
  // Type-specific styles - Info (blue)
  info: "group toast group-[.toaster]:!bg-blue-50 group-[.toaster]:!text-blue-900 group-[.toaster]:!border-blue-200 dark:group-[.toaster]:!bg-blue-950 dark:group-[.toaster]:!text-blue-100 dark:group-[.toaster]:!border-blue-800",
} as const

/**
 * Custom icons for each toast type with appropriate colors
 */
const toastIcons = {
  success: (
    <CheckCircle2 className="size-5 text-emerald-600 dark:text-emerald-400" />
  ),
  error: <AlertCircle className="size-5 text-red-600 dark:text-red-400" />,
  warning: (
    <AlertTriangle className="size-5 text-amber-600 dark:text-amber-400" />
  ),
  info: <Info className="size-5 text-blue-600 dark:text-blue-400" />,
  loading: (
    <Loader2 className="size-5 animate-spin text-muted-foreground" />
  ),
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      position={ToastConfig.position}
      visibleToasts={ToastConfig.maxVisible}
      toastOptions={{
        classNames: {
          toast: toastStyles.toast,
          description: toastStyles.description,
          actionButton: toastStyles.actionButton,
          cancelButton: toastStyles.cancelButton,
          closeButton: toastStyles.closeButton,
          success: toastStyles.success,
          error: toastStyles.error,
          warning: toastStyles.warning,
          info: toastStyles.info,
        },
      }}
      icons={toastIcons}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

export { Toaster, toastStyles }
